import { UserRole } from "@features/settings/types/settings.types";
import { ConfirmationModal } from "@shared/components/ConfirmationModal";
import { Button } from "@shared/components/ui/Button";
import { useProfile } from "@shared/hooks/useProfile";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { AuthorService, AuthorStats } from "../services/author.service";
import { SpaceService } from "../services/space.service";
import { useSpacesStore } from "../store/useSpacesStore";
import AuthorInfo from "./AuthorInfo";
import { CreateSpaceModal } from "./CreateSpaceModal";
import PopularSpaces from "./PopularSpaces";
import UpcomingEvents from "./UpcomingEvents";

interface SpaceRightContentProps {
  onCreateSpace?: () => void;
  authorId?: string;
  hideAuthorInfo?: boolean;
}

/**
 * Component that displays the right sidebar content in the spaces section
 */
function SpaceRightContent({
  onCreateSpace,
  authorId,
  hideAuthorInfo = false,
}: SpaceRightContentProps) {
  const {
    popularSpacesByInteractions,
    isLoadingPopularByInteractions,
    fetchPopularSpacesByInteractions,
    currentSpace,
  } = useSpacesStore();

  // State for author stats
  const [authorStats, setAuthorStats] = useState<AuthorStats | null>(null);
  const [isLoadingAuthorStats, setIsLoadingAuthorStats] =
    useState<boolean>(false);

  // State for delete confirmation modal
  const [showDeleteConfirmation, setShowDeleteConfirmation] =
    useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // State for edit modal
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // User profile for permissions
  const { user } = useProfile();
  const navigate = useNavigate();

  // Check if user can delete the space
  const canEdit =
    user &&
    currentSpace &&
    (currentSpace.author?.id === user.id || user.role === UserRole.ADMIN);

  const canDelete = canEdit;

  // Use a ref to track if this is the first render
  const isFirstRender = useRef(true);

  // Determine which author ID to use
  const displayAuthorId = authorId || currentSpace?.author?.id;

  // Fetch popular spaces on mount
  useEffect(() => {
    fetchPopularSpacesByInteractions(5);
    isFirstRender.current = false;
  }, [fetchPopularSpacesByInteractions]);

  // Fetch author stats when we have an author ID
  useEffect(() => {
    if (displayAuthorId && currentSpace?.author) {
      setIsLoadingAuthorStats(true);

      AuthorService.getAuthorStats(displayAuthorId)
        .then((stats) => {
          setAuthorStats(stats);
          setIsLoadingAuthorStats(false);
        })
        .catch((error) => {
          console.error("Error fetching author stats:", error);
          setIsLoadingAuthorStats(false);
        });
    }
  }, [displayAuthorId, currentSpace?.author]);

  // Handle delete confirmation
  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!currentSpace?.id) return;

    try {
      setIsDeleting(true);
      await SpaceService.deleteSpace(currentSpace.id);
      toast.success("Space deleted successfully");
      navigate("/spaces");
    } catch (error) {
      console.error("Error deleting space:", error);
      toast.error("Failed to delete space");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  // Handle edit space
  const onEditSpace = () => {
    setShowEditModal(true);
  };

  return (
    <div className="w-full hidden xl:block xl:w-72">
      <div className="lg:sticky lg:top-16 lg:h-[calc(100dvh-64px)] lg:overflow-x-hidden lg:overflow-y-auto no-scrollbar">
        <div className="md:py-8">
          {/* Buttons */}
          <div className="mb-4">
            <Button variant="primary" fullWidth onClick={onCreateSpace}>
              Create Space
            </Button>
          </div>

          {canEdit && currentSpace && (
            <div className="mb-4">
              <Button variant="outline" fullWidth onClick={onEditSpace}>
                Edit Space
              </Button>
            </div>
          )}

          {canDelete && currentSpace && (
            <div className="mb-4">
              <Button variant="danger" fullWidth onClick={handleDeleteClick}>
                Delete Space
              </Button>
            </div>
          )}

          {/* Blocks */}
          <div className="space-y-4">
            {/* Author Info - Only show if we have an author and hideAuthorInfo is false */}
            {!hideAuthorInfo &&
              currentSpace?.author &&
              authorStats &&
              currentSpace.author.id && (
                <AuthorInfo author={currentSpace.author} stats={authorStats} />
              )}

            {/* Upcoming Events */}
            <UpcomingEvents />

            {/* Popular Spaces */}
            <PopularSpaces
              spaces={popularSpacesByInteractions}
              isLoading={isLoadingPopularByInteractions}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title="Delete Space"
        message="Are you sure you want to delete this space? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Edit Space Modal */}
      {currentSpace && (
        <CreateSpaceModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          space={currentSpace}
        />
      )}
    </div>
  );
}

export default SpaceRightContent;
